import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { aggregatesApi, categoriesApi, type AggregateDetail, type Category } from '../lib/domain';
import { ApiError } from '../lib/api';
import { useScope } from '../lib/hooks';
import { useUnits } from '../lib/units';

export function AggregateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [scope] = useScope();
  const units = useUnits(scope);
  const [aggregate, setAggregate] = useState<AggregateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [description, setDescription] = useState('');
  const [shoppingNote, setShoppingNote] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    aggregatesApi
      .get(id)
      .then((r) => {
        setAggregate(r.aggregate);
        setDescription(r.aggregate.description ?? '');
        setShoppingNote(r.aggregate.shoppingNote ?? '');
        setCategoryId(r.aggregate.categoryId ?? '');
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Sammelprodukt konnte nicht geladen werden.'));
  }, [id]);

  useEffect(() => {
    if (scope) categoriesApi.list(scope).then((r) => setCategories(r.categories)).catch(() => setCategories([]));
  }, [scope]);

  async function save() {
    if (!id) return;
    setSaveState('saving');
    setSaveError(null);
    try {
      await aggregatesApi.update(id, {
        description: description.trim() || null,
        shoppingNote: shoppingNote.trim() || null,
        categoryId: categoryId || null,
      });
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      setSaveError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.');
    }
  }

  if (error) return <p className="error-text" role="alert">{error}</p>;
  if (!aggregate) return <p>Lade…</p>;

  return (
    <div className="stack">
      <h1>{aggregate.name}</h1>
      <Link to="/products">← Alle Produkte</Link>

      <div className="card stack">
        <h2>Eigenschaften</h2>
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          Im Einkaufsmodus verhält sich das Sammelprodukt wie ein Produkt: Es wird in seiner Kategorie einsortiert und zeigt Beschreibung und Einkaufshinweis an.
        </p>
        <div className="field">
          <label htmlFor="aggregate-category">Kategorie</label>
          <select id="aggregate-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Unkategorisiert</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="aggregate-description">Beschreibung</label>
          <textarea id="aggregate-description" rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="aggregate-shopping-note">Einkaufsnotiz</label>
          <textarea id="aggregate-shopping-note" rows={3} maxLength={2000} value={shoppingNote} onChange={(e) => setShoppingNote(e.target.value)} />
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            Wird im Einkaufsmodus als Hinweis angezeigt.
          </p>
        </div>
        <button className="primary" onClick={save} disabled={saveState === 'saving'}>
          {saveState === 'saving' ? 'Speichere…' : 'Speichern'}
        </button>
        {saveState === 'saved' && <p className="muted" role="status">Gespeichert.</p>}
        {saveError && <p className="error-text" role="alert">{saveError}</p>}
      </div>

      <div className="card stack">
        <h2>Enthaltene Produkte</h2>
        <ul className="list-reset stack">
          {aggregate.products.map((p) => (
            <li key={p.id}>
              <Link to={`/products/${p.id}`}>{p.name}</Link>
              {p.brand && <span className="muted"> · {p.brand}</span>}
            </li>
          ))}
          {aggregate.products.length === 0 && <p className="muted">Noch keine Produkte zugeordnet. Das geht in der Produktbearbeitung.</p>}
        </ul>
      </div>

      <div className="card stack">
        <h2>Kaufhistorie</h2>
        {aggregate.purchaseHistory.length === 0 && <p className="muted">Noch keine Käufe.</p>}
        <ul className="list-reset stack">
          {aggregate.purchaseHistory.map((it) => {
            const store = it.purchase.storeBranch;
            return (
              <li key={it.id} className="row between">
                <div>
                  <Link to={`/products/${it.product.id}`}>{it.product.name}</Link>
                  <div className="muted">
                    {new Date(it.purchase.purchasedAt).toLocaleDateString('de-DE')}
                    {store && ` · ${store.chain?.name ?? '?'} - ${store.name}`}
                    {it.isPromo && ' · Aktion'}
                  </div>
                </div>
                <div>
                  {it.unitPrice != null ? `${Number(it.unitPrice).toFixed(2)} €` : 'kein Preis'}
                  <div className="muted">
                    {Number(it.quantity)} {units.label(it.unit)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
